import { useState } from "react";
import {
  Badge,
  Button,
  Col,
  DropdownItem,
  DropdownMenu,
  DropdownToggle,
  PopoverBody,
  Row,
  UncontrolledDropdown,
  UncontrolledPopover
} from "reactstrap";
import brokerIcon from "../../../../assets/images/iconsRoles/brokerIcon.svg";
import employerIcon from "../../../../assets/images/iconsRoles/employerGroupIcon.svg";
import gaIcon from "../../../../assets/images/iconsRoles/GaIcon.svg";
import nationalIcon from "../../../../assets/images/iconsRoles/nationalIcon.svg";
import UserStatusBadge from "../../../../Components/Badges/UserStatusBadge";
import ButtonBack from "../../../../Components/ButtonBack";
import EditEmployeeModal from "../../../../Components/modals/EmployeesModal/EditEmployeeModal";
import EditStatusEmployeeModal from "../../../../Components/modals/EmployeesModal/EditStatusEmployeeModal";
import UserInfoCard from "../../../../Components/UserInfoCard/UserInfoCard";
import {
  DASHBOARD_USER_ROLES,
  employeeStatusLabels,
  statusToBadgeColor,
} from "../../../../constants";
import { useReferralLinkBuilder } from "../../../../hooks/useReferralLinkBuilder";
import { useCurrentUser } from "../../../../slices/auth/hooks";
import { formatDate } from "../../../../utils/utils";

const ProfileEmployee = ({
  employee,
  loading,
  setEmployee,
  onTerminate,
  onReactivate,
  onDelete,
  onRefreshTable,
}) => {
  const currentUser = useCurrentUser();

  const [openEditModal, setOpenEditModal] = useState(false);
  const [openModalEditStatus, setOpenModalEditStatus] = useState(false);

  const [isCodeCopied, setIsCodeCopied] = useState(false);

  const {
    createEmployeeReferralLink,
  } = useReferralLinkBuilder();

  const employeeReferralLink = employee ? createEmployeeReferralLink({
    employeeCode: employee.employeeCode,
    employerCode: employee.employerCode,
  }) : "";

  const renderIcon = (role) => {
    switch (role) {
      case DASHBOARD_USER_ROLES.NATIONAL:
        return nationalIcon;
      case DASHBOARD_USER_ROLES.GA:
        return gaIcon;
      case DASHBOARD_USER_ROLES.BROKER:
        return brokerIcon;
      case DASHBOARD_USER_ROLES.EMPLOYER:
        return employerIcon;
      default:
        return "";
    }
  };
  const linkToProfile = (role) => {
    switch (role) {
      case DASHBOARD_USER_ROLES.NATIONAL:
        return "national";
      case DASHBOARD_USER_ROLES.GA:
        return "ga";
      case DASHBOARD_USER_ROLES.BROKER:
        return "brokers";
      case DASHBOARD_USER_ROLES.EMPLOYER:
        return "employers";
      default:
        return "";
    }
  };

  const handleCopyInviteCode = () => {
    setIsCodeCopied(true);
    navigator.clipboard.writeText(employeeReferralLink);
    setTimeout(() => {
      setIsCodeCopied(false);
    }, 3000);
  };

  const isTerminated = employee?.status === "terminated";
  const isInvited = employee?.status === "active" && !employee?.employeeCodeUsed

  const isAdmin = currentUser.role === DASHBOARD_USER_ROLES.ADMIN;

  const itemToEditStatus = employee;

  const handleEditClick = () => {
    setOpenEditModal(true);
  };

  const handleTerminateEmployee = () => {
    onTerminate(employee);
  };

  const handelReactivateEmployee = () => {
    onReactivate(employee);
  };

  const handleRemoveEmployee = () => {
    onDelete(employee);
  };

  const renderDropdownMenu = (item) => {
    return (
      <DropdownMenu>
        {!isTerminated && (
          <DropdownItem
            onClick={() => handleEditClick(item)}
            className="edit-item-btn"
          >
            <i className="ri-pencil-fill align-bottom me-2 text-muted"></i>
            Edit
          </DropdownItem>
        )}
        {!isTerminated && (
          <DropdownItem
            onClick={() => handleTerminateEmployee(item)}
            className="d-flex align-items-center"
          >
            <i className="ri-close-line ms-n1 me-2 fs-4 text-muted "></i>
            Terminate
          </DropdownItem>
        )}
        {isTerminated && (
          <DropdownItem
            onClick={() => handelReactivateEmployee(item)}
            className="d-flex align-items-center"
          >
            <i className="ri-information-line ms-n1 me-2 fs-4 text-muted"></i>
            Reactivate
          </DropdownItem>
        )}
        {isTerminated && (
          <DropdownItem
            onClick={() => handleRemoveEmployee(item)}
            className="remove-item-btn"
          >
            {" "}
            <i className="ri-delete-bin-fill align-bottom me-2 text-muted"></i>{" "}
            Delete Completely
          </DropdownItem>
        )}
        {isAdmin && (
          <DropdownItem onClick={() => setOpenModalEditStatus(true, item)}>
            <i className="ri-pencil-fill align-bottom me-2 text-muted"></i>
            Edit Status
          </DropdownItem>
        )}
      </DropdownMenu>
    );
  };
  const renderStatusBadge = (item) => {
    const label = employeeStatusLabels[item.status] || "Unknown";
    const badgeColor = statusToBadgeColor[item.status] || "secondary";
    const isInvited =
      item.status === "active" && !item.employeeCodeUsed;

    if (isInvited && isAdmin) {
      return (
        <div className="d-flex flex-column align-items-start">
          <UserStatusBadge
            color="warning"
            status="Invited" fontSize="11" />
        </div>
      );
    }

    if (isInvited && !isAdmin) {
      return (
        <div className="d-flex flex-column align-items-start">
          <UserStatusBadge
            color="success"
            status="Active" fontSize="11" />
        </div>
      );
    }

    return <UserStatusBadge status={label} color={`${badgeColor}`} />;
  };

  const renderTierBadge = (item) => {
    const isFamily = item.isFamily || item.familyPlan;
    return (
      <Badge color={isFamily ? "info" : "secondary"} pill>
        {isFamily ? "Family (F)" : "Employee (E)"}
      </Badge>
    );
  };

  if (loading || !employee) {
    return (
      <div className="page-content">
        <div
          className="text-center d-flex justify-content-center align-items-center"
          style={{ minHeight: "150px", height: "150px" }}
        >
          <div className="spinner-border spinner-border-lg mr-3"></div>
        </div>
      </div>
    );
  }

  const userInfo = employee.userInfo?.companyName
    ? employee.userInfo?.companyName
    : `${employee.userInfo?.firstName || ""} ${employee.userInfo?.lastName || ""
    } `;

  return (
    <>
      {openEditModal && (
        <EditEmployeeModal
          open={openEditModal}
          editUser={employee}
          setOpen={setOpenEditModal}
          setUser={setEmployee}
          onRefreshTable={onRefreshTable}
        />
      )}
      <EditStatusEmployeeModal
        open={openModalEditStatus}
        setOpen={setOpenModalEditStatus}
        item={itemToEditStatus}
        onRefreshTable={onRefreshTable}
      />
      <div className="page-content">
        <div className="card">
          <div className="card-body pb-5">
            <div className="d-flex justify-content-between align-items-center mb-4">
              <ButtonBack />
              <UncontrolledDropdown className="dropdown d-inline-block">
                <DropdownToggle
                  tag="button"
                  className="btn btn-soft-primary fw-normal border border-primary btn-xl"
                >
                  Manage
                </DropdownToggle>
                {renderDropdownMenu(employee)}
              </UncontrolledDropdown>
            </div>
            <Row className="d-flex align-items-start">
              <Col xl={4}>
                <Col xl={8}>
                  <div className=" border border-1 rounded">
                    <div
                      style={{ background: "var(--thead-bg)" }}
                    >
                      <h5 className="mb-0 p-3 ">
                        Employee: {employee.firstName} {employee.lastName}
                      </h5>
                    </div>
                    <div className=" px-3 pt-4 pb-0">
                      <h4 className="mb-2">
                        {employee.firstName} {employee.lastName}
                      </h4>
                      <h6 className="mb-0 fw-normal text-muted">
                        {employee.email}
                      </h6>
                      <hr className="text-muted mx-1" />
                      <h6 className="fw-normal">
                        {employee.employeeCode ? "Date Added on" : "Invited On"}
                        : {formatDate(employee.createdAt)}
                      </h6>
                      <h6 className="d-flex align-items-center">
                        Status:
                        <div className="ms-2">
                          {renderStatusBadge(employee)}
                        </div>
                      </h6>
                      <h6 className="d-flex align-items-center">
                        Tier:
                        <div className="ms-2">
                          {renderTierBadge(employee)}
                        </div>
                      </h6>
                    </div>
                  </div>
                </Col>
              </Col>

              <Col xl={6} className="mt-4 mt-xxl-0">
                {employee.nationalInfo && (
                  <div className="w-100 mb-3">
                    <UserInfoCard
                      info={employee.nationalInfo}
                      role={DASHBOARD_USER_ROLES.NATIONAL}
                      linkToProfile={linkToProfile}
                      invitedByUserId={employee.nationalInfo}
                      icon={renderIcon(DASHBOARD_USER_ROLES.NATIONAL)}
                    />
                  </div>
                )}

                {employee.gaInfo && (
                  <div className="w-100 mb-3">
                    <UserInfoCard
                      info={employee.gaInfo}
                      role={DASHBOARD_USER_ROLES.GA}
                      linkToProfile={linkToProfile}
                      invitedByUserId={employee.employerId}
                      icon={renderIcon(DASHBOARD_USER_ROLES.GA)}
                    />
                  </div>
                )}
                {employee.brokerInfo && (
                  <div className="w-100 mb-3">
                    <UserInfoCard
                      info={employee.brokerInfo}
                      role={DASHBOARD_USER_ROLES.BROKER}
                      linkToProfile={linkToProfile}
                      invitedByUserId={employee.employerId}
                      icon={renderIcon(DASHBOARD_USER_ROLES.BROKER)}
                    />
                  </div>
                )}
                {employee.employerInfo && (
                  <div className="w-100 mb-3">
                    <UserInfoCard
                      info={employee.employerInfo}
                      role={DASHBOARD_USER_ROLES.EMPLOYER}
                      // linkToProfile={linkToProfile}
                      invitedByUserId={employee.employerId}
                      icon={renderIcon(DASHBOARD_USER_ROLES.BROKER)}
                    />
                  </div>
                )}
                {employeeReferralLink && (
                  <div className="w-100 mb-4">
                    <h5 className="mb-2 ms-1 fw-semibold">Employee Unique Link:</h5>
                    <div className="border rounded-4 p-3 d-flex flex-column flex-md-row align-items-start align-items-md-center justify-content-between gap-3 bg-light">
                      <div className="text-break text-muted flex-grow-1">
                        <a target="_blank" href={employeeReferralLink}>
                          {employeeReferralLink}
                        </a>
                      </div>
                      <div className="d-flex align-items-center gap-2">
                        <Button
                          id="copyInviteCode"
                          onClick={handleCopyInviteCode}
                          color="soft-primary"
                          size="sm"
                          className="border border-primary d-flex align-items-center"
                        >
                          {isCodeCopied ? (
                            <>
                              <i className="ri-check-line me-2" />
                              Copied!
                            </>
                          ) : (
                            <>
                              <i className="ri-file-copy-line me-2" />
                              Copy
                            </>
                          )}
                        </Button>
                        <UncontrolledPopover
                          onClick={(e) => e.stopPropagation()}
                          placement="bottom"
                          target="copyInviteCode"
                          trigger="hover"
                        >
                          <PopoverBody className="p-2">
                            {isCodeCopied ? "Copied!" : "Copy to clipboard"}
                          </PopoverBody>
                        </UncontrolledPopover>
                      </div>
                    </div>
                  </div>
                )}


                {employee.userInfo && (
                  <>
                    <h5 className="mb-2 ms-1">Broker: {userInfo}</h5>
                    <div className="border rounded-4 p-3">
                      <span className="text-muted">
                        {employee.inviteCode && `(${employee.inviteCode})`}
                      </span>
                    </div>
                  </>
                )}
                {employee.appUserInfo ? (
                  <>
                    <h5 className="mb-2 ms-1">Used by:</h5>
                    <div className="border rounded-4  p-3">
                      <span className="text-muted fw-inhert">
                        {employee.appUserInfo.name} (
                        {employee.appUserInfo.email})
                      </span>
                    </div>
                  </>
                ) : null}
              </Col>
              <Col xl={2}>
                <div className="d-flex justify-content-end top-0">
                  <Button onClick={() => handleEditClick(employee)} color="primary">
                    Edit Information
                  </Button>
                </div>
              </Col>
            </Row>
          </div>
        </div>
      </div>
    </>
  );
};

export default ProfileEmployee;